// src/pages/Login.js
import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { EventType, InteractionStatus } from '@azure/msal-browser';
import { useNavigate } from 'react-router-dom';

const Login = () => {
  const { instance, accounts, inProgress } = useMsal();
  const navigate = useNavigate();

  const msalAuthenticated = accounts.length > 0;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [customAuth, setCustomAuth] = useState(false);
  const [error, setError] = useState('');
  const [userStatus, setUserStatus] = useState(null);
  const [msalLoggingIn, setMsalLoggingIn] = useState(false);

  const isAuthenticated = msalAuthenticated || customAuth;

  // Ensure MSAL sets an active account after successful login
  useEffect(() => {
    const cbId = instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
        instance.setActiveAccount(event.payload.account);
      }
    });
    return () => { if (cbId) instance.removeEventCallback(cbId); };
  }, [instance]);

  // Restore auth state (custom + msal) and pull user status for custom logins
  useEffect(() => {
    const storedAuth = localStorage.getItem('custom-auth');
    const storedEmail = localStorage.getItem('custom-email');
    const msalAccount = instance.getActiveAccount() || accounts[0];

    // Restore custom login
    if (storedAuth === 'true' && storedEmail) {
      setCustomAuth(true);
      setEmail(storedEmail);
      localStorage.setItem('user-email', storedEmail);

      fetch(`/api/get-status?email=${encodeURIComponent(storedEmail)}`)
        .then(res => res.json())
        .then(data => setUserStatus(data.status))
        .catch(err => {
          console.error('❌ Error fetching user status:', err);
          setUserStatus(null);
        });
    }

    // Restore MSAL email for UI convenience
    if (msalAccount?.username) {
      localStorage.setItem('user-email', msalAccount.username);
    }
  }, [accounts, instance]);

  // 🔁 If already authenticated and we're on /login, route them appropriately
  useEffect(() => {
    if (!isAuthenticated) return;

    // MSAL users → Home
    if (msalAuthenticated) {
      navigate('/', { replace: true });
      return;
    }

    // Custom users → route by status once known
    if (customAuth && userStatus !== null) {
      if (userStatus === 'Active') {
        navigate('/', { replace: true });
      } else if (userStatus === 'New') {
        navigate('/set-password', { replace: true });
      }
      // Locked/Unknown: stay on /login (shows message below)
    }
  }, [isAuthenticated, msalAuthenticated, customAuth, userStatus, navigate]);

  // --- Microsoft login flow (popup) ---
  const handleMsalLogin = async (e) => {
    e?.preventDefault();

    // Block while MSAL is doing anything or if we've already started
    if (msalLoggingIn || inProgress !== InteractionStatus.None) return;
    setMsalLoggingIn(true);

    try {
      const resp = await instance.loginPopup({ prompt: 'select_account' });

      const acct =
        resp?.account ||
        instance.getActiveAccount() ||
        instance.getAllAccounts()[0];

      if (!acct) throw new Error('No account after MSAL login');
      instance.setActiveAccount(acct);

      const msalEmail =
        acct.username ||
        acct.idTokenClaims?.preferred_username ||
        acct.idTokenClaims?.email;

      if (!msalEmail) throw new Error('MSAL account missing username/email');

      // Mirror for UI
      localStorage.setItem('user-email', msalEmail);

      // Ask the backend to set your orion_session cookie
      const r = await fetch('/api/msal-login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: msalEmail }),
      });

      if (!r.ok) {
        const txt = await r.text().catch(() => '');
        throw new Error(`/api/msal-login ${r.status}: ${txt}`);
      }

      // Hard redirect after cookie is set so protected calls see it immediately
      window.location.assign('/');
    } catch (err) {
      const code = err?.errorCode || err?.code;
      // These are common/harmless in popup flows; log others
      if (code !== 'hash_empty_error' &&
          code !== 'user_cancelled' &&
          code !== 'interaction_in_progress') {
        console.error('❌ Microsoft login error:', err);
      }
    } finally {
      setMsalLoggingIn(false);
    }
  };

  // --- Custom email/password login ---
  const handleCustomLogin = async () => {
    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: email, password })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const errMsg = errData.message || 'Login failed';
        console.warn('⚠️ Login error:', errMsg);
        throw new Error(errMsg);
      }

      const data = await res.json();

      localStorage.setItem('custom-auth', 'true');
      localStorage.setItem('custom-email', email);
      localStorage.setItem('user-email', email);
      setCustomAuth(true);

      // Redirect (use hard redirect so any new cookies are honored immediately)
      setTimeout(() => {
        if (data.redirect) {
          window.location.assign(data.redirect);
        } else {
          window.location.assign('/');
        }
      }, 100);
    } catch (err) {
      console.error('❌ Unexpected login error:', err);
      setError(err.message);
    }
  };

  

  return (
    <div>
      <h1>Welcome</h1>
      <h2>Please Login with Email or Microsoft To Access The Site</h2>

      {!isAuthenticated && (
        <div className="login-section">

<div
            style={{
              marginTop: '2rem',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                flexWrap: 'wrap'
              }}
            >
              <input
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                style={{
                  padding: '12px',
                  fontSize: '1.2rem',
                  borderRadius: '6px',
                  border: '1px solid #ccc',
                  width: '250px'
                }}
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={{
                  padding: '12px',
                  fontSize: '1.2rem',
                  borderRadius: '6px',
                  border: '1px solid #ccc',
                  width: '250px'
                }}
              />
              <button
                type="button"
                onClick={handleCustomLogin}
                style={{
                  padding: '12px 24px',
                  fontSize: '1.2rem',
                  backgroundColor: '#cab069',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: '#222',
                  whiteSpace: 'nowrap'
                }}
              >
                Login with Email
              </button>
              {error && (
                <p style={{ color: 'red', fontSize: '1rem', width: '100%' }}>{error}</p>
              )}
            </div>
          </div>
          

          <button
            type="button"
            className="login-btn"
            onClick={handleMsalLogin}
            disabled={msalLoggingIn || inProgress !== InteractionStatus.None}
            style={{
              transform: 'scale(1)',
              transformOrigin: 'top left',
              marginBottom: '1rem',
              marginTop: '30px',
              justifyContent: 'center',
              opacity: (msalLoggingIn || inProgress !== InteractionStatus.None) ? 0.6 : 1,
              cursor: (msalLoggingIn || inProgress !== InteractionStatus.None) ? 'not-allowed' : 'pointer'
            }}
          >
            {msalLoggingIn || inProgress !== InteractionStatus.None
              ? 'Signing in…'
              : 'Login with Microsoft'}
          </button>
        </div>
      )}

      {isAuthenticated && (
        <>
          {userStatus === 'Active' && <p>You are logged in.</p>}
          {userStatus === 'New' && <p>You must set your password to proceed.</p>}
          {userStatus === 'Locked' && <p>Your account is locked. Please contact support.</p>}
          {!userStatus && <p>Loading status...</p>}
        </>
      )}
    </div>
  );
};

export default Login;
